import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    linkedSignal,
    signal,
    ViewEncapsulation,
} from "@angular/core";
import { applyEach, disabled, email, FormField, form, required, submit, validate } from "@angular/forms/signals";
import { FormsModule } from "@angular/forms";
import { ModalComponent } from "../../modal/modal.component";
import { ModalHeaderComponent } from "../../modal/modal.header.component";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { GuestRegistrant } from "./attendance.service";
import { SeAttendanceStore } from "./se.attendance.store";
import { S25Util } from "../../../util/s25-util";
import { S25CheckboxModule } from "../../s25-checkbox/s25.checkbox.module";
import { ResponseMessage } from "../../../dataaccess/data.access";
import { SEEventDetailsStore } from "../event/se.event.details.store";
import { Bind } from "../../../decorators/bind.decorator";
import { DBContact } from "../../student-housing/student.housing.service";

type GuestOccurrenceField = { id: number; label: string; included: boolean };

type GuestRegistrationFormModel = {
    firstName: string;
    familyName: string;
    email: string;
    occurrences: GuestOccurrenceField[];
};

@Component({
    standalone: true,
    template: `
        <s25-modal-header [data]="params()"></s25-modal-header>
        <form class="modal-body" (ngSubmit)="onSubmit()" (keydown.enter)="onSubmit()">
            @if (hasRegistrationForm()) {
                <p class="intro">Enter your name and email. We will send you a link to complete registration.</p>
            } @else {
                <p class="intro">
                    Enter your name and email, then choose which dates to register for. We will send you a link to
                    complete registration.
                </p>
            }
            <div class="field-grid">
                @let firstNameField = registrationForm.firstName();
                <label class="field" [class.field--error]="showFieldErrors(firstNameField)">
                    <span class="field-label">First name <span class="required">*</span></span>
                    <input
                        class="cn-form__control"
                        type="text"
                        [formField]="registrationForm.firstName"
                        autocomplete="given-name"
                    />
                    @if (showFieldErrors(firstNameField)) {
                        <span class="field-error">{{ firstNameField.errors()[0].message }}</span>
                    }
                </label>

                @let familyNameField = registrationForm.familyName();
                <label class="field" [class.field--error]="showFieldErrors(familyNameField)">
                    <span class="field-label">Last name <span class="required">*</span></span>
                    <input
                        class="cn-form__control"
                        type="text"
                        [formField]="registrationForm.familyName"
                        autocomplete="family-name"
                    />
                    @if (showFieldErrors(familyNameField)) {
                        <span class="field-error">{{ familyNameField.errors()[0].message }}</span>
                    }
                </label>

                @let emailField = registrationForm.email();
                <label class="field field--full" [class.field--error]="showFieldErrors(emailField)">
                    <span class="field-label">Email <span class="required">*</span></span>
                    <input
                        class="cn-form__control"
                        type="email"
                        [formField]="registrationForm.email"
                        autocomplete="email"
                    />
                    @if (showFieldErrors(emailField)) {
                        <span class="field-error">{{ emailField.errors()[0].message }}</span>
                    }
                </label>
            </div>
            @if (!hasRegistrationForm()) {
                <div class="occurrences">
                    <div class="occurrences-header">
                        <span class="occurrences-label">Dates <span class="required">*</span></span>
                        @if (occurrenceChoices().length > 3) {
                            <s25-ng-button
                                [type]="'link'"
                                [onClick]="selectAllOccurrences"
                                [disabled]="registrationForm().submitting()"
                            >
                                Select all
                            </s25-ng-button>
                        }
                    </div>
                    <div class="occurrences-grid">
                        @for (occurrence of registrationForm.occurrences; track occurrence) {
                            <s25-ng-shift-selectable-checkbox
                                [formField]="occurrence.included"
                                [index]="$index"
                                [group]="'se-guest-register-occurrences'"
                            >
                                {{ occurrence().value().label }}
                            </s25-ng-shift-selectable-checkbox>
                        }
                    </div>
                    @if (showOccurrenceErrors()) {
                        @for (error of registrationForm.occurrences().errors(); track $index) {
                            <span class="field-error">{{ error.message }}</span>
                        }
                    }
                </div>
            }
            @if (submitError()) {
                <p class="submit-error">{{ submitError() }}</p>
            }
            <div class="modal-footer">
                <s25-ng-button [type]="'primary'" [onClick]="onSubmit" [disabled]="registrationForm().submitting()">
                    Continue
                </s25-ng-button>
                <s25-ng-button [type]="'outline'" [onClick]="onCancel" [disabled]="registrationForm().submitting()">
                    Cancel
                </s25-ng-button>
            </div>
        </form>
    `,
    styles: `
        .intro,
        .field-grid {
            margin: 0 0 1rem;
        }

        .field-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 0.75rem 1rem;
            align-items: start;
        }

        .field {
            display: grid;
            gap: 0.35rem;
            min-width: 0;
        }

        .field--full {
            grid-column: 1 / -1;
        }

        .field-label {
            font-weight: 500;
        }

        .required {
            color: var(--color--danger);
        }

        .field-error {
            color: var(--color--danger);
            font-size: 0.85rem;
        }

        .field--error .cn-form__control {
            border-color: var(--color--danger) !important;
        }

        :host ::ng-deep .cn-form__control {
            padding-right: 0.5em;
        }

        .occurrences {
            display: grid;
            gap: 0.5rem;
            margin-bottom: 1rem;
        }

        .occurrences-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 0.5rem;
        }

        .occurrences-label {
            font-weight: 500;
        }

        .occurrences-grid {
            display: grid;
            gap: 0.35rem;
        }

        .submit-error {
            margin: 0 0 0.75rem;
            color: var(--color--danger);
            font-size: 0.9rem;
        }

        .modal-footer {
            padding-inline: 0;
            padding-block-end: 0;
        }

        @media (max-width: 480px) {
            .field-grid {
                grid-template-columns: 1fr;
            }

            .field--full {
                grid-column: auto;
            }
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, FormField, ModalHeaderComponent, S25ButtonComponent, S25CheckboxModule],
})
export class SeGuestRegistrationRequestModalComponent extends ModalComponent {
    private readonly attendanceStore = inject(SeAttendanceStore);
    private readonly eventStore = inject(SEEventDetailsStore);

    readonly submitError = signal<string | undefined>(undefined);

    readonly hasRegistrationForm = computed(() => !!this.attendanceStore.registrationFormId());

    readonly occurrenceChoices = computed(() => {
        if (this.hasRegistrationForm()) return [];
        return (
            this.eventStore.occurrences()?.map((occurrence) => ({
                id: occurrence.reservationId,
                label: occurrence.date,
            })) ?? []
        );
    });

    readonly registrationModel = linkedSignal<GuestRegistrationFormModel>(() => {
        const choices = this.occurrenceChoices();
        return {
            firstName: "",
            familyName: "",
            email: "",
            occurrences: choices.map((choice) => ({
                id: choice.id,
                label: choice.label,
                included: choices.length === 1,
            })),
        };
    });

    readonly registrationForm = form(this.registrationModel, (schema) => {
        required(schema.firstName, { message: "First name is required" });
        required(schema.familyName, { message: "Last name is required" });
        required(schema.email, { message: "Email is required" });
        email(schema.email, { message: "Please enter a valid email address" });
        DBContact.Validators.firstName(schema.firstName);
        DBContact.Validators.familyName(schema.familyName);
        validate(schema.occurrences, (field) => {
            const occurrences = field.value();
            if (!occurrences.length) return;
            if (!occurrences.some((occurrence) => occurrence.included)) {
                return { kind: "error", message: "Select at least one date to register for" };
            }
        });

        disabled(schema.firstName, ({ stateOf }) => stateOf(schema).submitting());
        disabled(schema.familyName, ({ stateOf }) => stateOf(schema).submitting());
        disabled(schema.email, ({ stateOf }) => stateOf(schema).submitting());
        applyEach(schema.occurrences, (occurrence) => {
            disabled(occurrence.included, ({ stateOf }) => stateOf(schema).submitting());
        });
    });

    readonly submitAttempted = signal(false);

    showFieldErrors(field: { invalid: () => boolean; touched: () => boolean }): boolean {
        return (field.touched() || this.submitAttempted()) && field.invalid();
    }

    showOccurrenceErrors(): boolean {
        return this.submitAttempted() && this.registrationForm.occurrences().invalid();
    }

    @Bind
    selectAllOccurrences() {
        this.registrationModel.update((model) => ({
            ...model,
            occurrences: model.occurrences.map((occurrence) => ({ ...occurrence, included: true })),
        }));
    }

    onSubmit = () => {
        this.submitAttempted.set(true);
        const { invalid, submitting, dirty } = this.registrationForm();
        if (invalid() || submitting() || !dirty()) {
            return;
        }

        submit(this.registrationForm, async (registrationForm) => {
            const { firstName, familyName, email, occurrences } = registrationForm().value();
            const request: GuestRegistrant = {
                firstName: firstName.trim(),
                familyName: familyName.trim(),
                email: email.trim(),
            };
            if (!this.hasRegistrationForm()) {
                request.occurrenceIds = occurrences
                    .filter((occurrence) => occurrence.included)
                    .map((occurrence) => occurrence.id);
            }

            this.submitError.set(undefined);
            const [response, error] = await S25Util.Maybe(this.attendanceStore.registerGuest(request));
            if (error) {
                this.submitError.set(S25Util.errorText(error, "Registration failed. Please try again."));
                return;
            }

            this.params()?.closeModal(response as ResponseMessage);
        });
    };

    onCancel = () => {
        this.params()?.closeModal(undefined);
    };
}
